/**
 * Centralized API Service Layer
 * 
 * Provides a consistent interface for all API calls with:
 * - Automatic auth header injection
 * - Centralized error handling
 * - Request/response interceptors
 */

import axios from "axios";

const API_URL = window.location.origin;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - adds auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handles common error cases
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      // Only redirect if not already on login page
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    // Handle network errors (app went to sleep / connection dropped)
    if (!error.response && error.code === "ERR_NETWORK") {
      console.warn("Network error detected - connection may have been dropped during inactivity");
    }
    return Promise.reject(error);
  }
);

// ============= AUTH SERVICE =============
export const authService = {
  login: (credentials) => apiClient.post("/auth/login", credentials),
  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },
  getCurrentUser: () => apiClient.get("/users/me"),
};

// ============= JOBS SERVICE =============
export const jobsService = {
  // Core CRUD
  getAll: () => apiClient.get("/jobs"),
  getById: (jobId) => apiClient.get(`/jobs/${jobId}`),
  create: (jobData) => apiClient.post("/jobs", jobData),
  update: (jobId, jobData) => apiClient.put(`/jobs/${jobId}`, jobData),
  delete: (jobId) => apiClient.delete(`/jobs/${jobId}`),
  
  // Estimates
  updateLaborEstimates: (jobId, estimates) => 
    apiClient.put(`/jobs/${jobId}/labor-estimates`, { labor_estimates: estimates }),
  updateMaterialEstimates: (jobId, estimates) => 
    apiClient.put(`/jobs/${jobId}/material-estimates`, { material_estimates: estimates }),
  updateEquipmentEstimates: (jobId, estimates) => 
    apiClient.put(`/jobs/${jobId}/equipment-estimates`, { equipment_estimates: estimates }),
  populateFromBidSheet: (jobId) => 
    apiClient.post(`/jobs/${jobId}/populate-estimates`),
  
  // Supplier Invoices
  addSupplierInvoice: (jobId, invoice) => 
    apiClient.post(`/jobs/${jobId}/supplier-invoices`, invoice),
  updateSupplierInvoice: (jobId, invoiceId, invoice) => 
    apiClient.put(`/jobs/${jobId}/supplier-invoices/${invoiceId}`, invoice),
  deleteSupplierInvoice: (jobId, invoiceId) => 
    apiClient.delete(`/jobs/${jobId}/supplier-invoices/${invoiceId}`),
  
  // Equipment Invoices
  addEquipmentInvoice: (jobId, invoice) => 
    apiClient.post(`/jobs/${jobId}/equipment-invoices`, invoice),
  updateEquipmentInvoice: (jobId, invoiceId, invoice) => 
    apiClient.put(`/jobs/${jobId}/equipment-invoices/${invoiceId}`, invoice),
  deleteEquipmentInvoice: (jobId, invoiceId) => 
    apiClient.delete(`/jobs/${jobId}/equipment-invoices/${invoiceId}`),
  
  // Materials Tracking
  initMaterialsTracking: (jobId) => 
    apiClient.post(`/jobs/${jobId}/materials-tracking/init`),
  updateMaterialsTracking: (jobId, updates) => 
    apiClient.put(`/jobs/${jobId}/materials-tracking`, { updates }),
  
  // Billings
  addBilling: (jobId, billing) => 
    apiClient.post(`/jobs/${jobId}/billings`, billing),
  updateBilling: (jobId, billingId, billing) => 
    apiClient.put(`/jobs/${jobId}/billings/${billingId}`, billing),
  deleteBilling: (jobId, billingId) => 
    apiClient.delete(`/jobs/${jobId}/billings/${billingId}`),
  
  // Photos
  addPhoto: (jobId, photoData) => 
    apiClient.post(`/jobs/${jobId}/photos`, photoData),
  deletePhoto: (jobId, photoId) => 
    apiClient.delete(`/jobs/${jobId}/photos/${photoId}`),
};

// ============= DAILY ENTRIES SERVICE =============
export const dailyEntriesService = {
  getAll: (params) => apiClient.get("/daily-entries", { params }),
  getById: (entryId) => apiClient.get(`/daily-entries/${entryId}`),
  create: (entry) => apiClient.post("/daily-entries", entry),
  delete: (entryId) => apiClient.delete(`/daily-entries/${entryId}`),
  getComputedCosts: (jobId) => apiClient.get(`/daily-entries/job/${jobId}/computed-costs`),
};

// ============= EMPLOYEES SERVICE =============
export const employeesService = {
  getAll: () => apiClient.get("/employees"),
  getById: (employeeId) => apiClient.get(`/employees/${employeeId}`),
  create: (employee) => apiClient.post("/employees", employee),
  update: (employeeId, employee) => apiClient.put(`/employees/${employeeId}`, employee),
  delete: (employeeId) => apiClient.delete(`/employees/${employeeId}`),
};

// ============= USERS SERVICE =============
export const usersService = {
  getAll: () => apiClient.get("/users"),
  getById: (userId) => apiClient.get(`/users/${userId}`),
  create: (user) => apiClient.post("/users", user),
  update: (userId, user) => apiClient.put(`/users/${userId}`, user),
  delete: (userId) => apiClient.delete(`/users/${userId}`),
  changePassword: (userId, passwords) => apiClient.put(`/users/${userId}/change-password`, passwords),
};

// ============= SETTINGS SERVICE =============
export const settingsService = {
  getOptions: () => apiClient.get("/settings/options"),
  updateOptions: (options) => apiClient.put("/settings/options", options),
  getMaterials: () => apiClient.get("/settings/materials"),
  addMaterial: (material) => apiClient.post("/settings/materials", material),
  updateMaterial: (materialId, material) => apiClient.put(`/settings/materials/${materialId}`, material),
  deleteMaterial: (materialId) => apiClient.delete(`/settings/materials/${materialId}`),
};

// ============= DASHBOARD SERVICE =============
export const dashboardService = {
  getStats: () => apiClient.get("/dashboard/stats"),
};

// ============= REPORTS SERVICE =============
export const reportsService = {
  getHoursReport: (params) => apiClient.get("/reports/hours", { params }),
  getDailyReport: (params) => apiClient.get("/reports/daily", { params }),
  getProfitLoss: (params) => apiClient.get("/reports/profit-loss", { params }),
  getAnalytics: (params) => apiClient.get("/analytics", { params }),
};

// ============= BID SHEETS SERVICE =============
export const bidSheetsService = {
  create: (jobId, bidSheet) => apiClient.post(`/jobs/${jobId}/bid-sheets`, bidSheet),
  update: (jobId, bidSheetId, bidSheet) => apiClient.put(`/jobs/${jobId}/bid-sheets/${bidSheetId}`, bidSheet),
  delete: (jobId, bidSheetId) => apiClient.delete(`/jobs/${jobId}/bid-sheets/${bidSheetId}`),
  duplicate: (jobId, bidSheetId) => apiClient.post(`/jobs/${jobId}/bid-sheets/${bidSheetId}/duplicate`),
  accept: (jobId, bidIds) => apiClient.post(`/jobs/${jobId}/accept-bids`, { bid_ids: bidIds }),
};

// Export the base client for custom requests
export default apiClient;
